import React, { PureComponent, Suspense } from 'react';

import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import UnauthedHeader from '@components/UnauthedHeader';
import { Loading } from 'Common/components/Atoms/Loading';
import {
  Button,
  Card,
  Col,
  Flex,
  Input,
  InputField,
  Link,
  Div,
  ContentLoader,
} from 'Common';
import { Text } from 'Common/components/Atoms/Text';
import OAuthButton from '@components/OAuthButton';
import withTheme from '@mui/styles/withTheme';
import withStyles from '@mui/styles/withStyles';
import queryString from 'qs';
import { Redirect } from 'react-router-dom';
import { actions as signupsActions } from '@reducers/signups';
import CompanyFilter from '@components/CompanyFilter';
import debounce from 'debounce-promise';
import * as Api from '@utils/Api';
import { retry } from '@utils/retry';
import { accountSelector, isLoadingAccountSelector } from 'selectors/Account';
import { generators } from 'stores';
import { isOn } from 'utils/featureFlags/global/isOn';
import { GlobalFeatureFlag } from 'constants/FeatureFlags';

const DEBOUNCE_INTERVAL = 500;

const styles = (theme) => ({
  line: {
    flex: '1 1',
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
  },
  companySelect: {
    '& input': {
      opacity: '1 !important',
    },
  },
  promoPhoto: {
    alignSelf: 'center !important',
    '& img': {
      height: '3em',
    },
  },
});

const PasswordStrengthBar = React.lazy(() =>
  retry(() => import('react-password-strength-bar'))
);

export class DeveloperSignUp extends PureComponent {
  constructor(props) {
    super(props);
    const { fetchRef, fetchPromo } = props;
    const query = queryString.parse(this.props.location.search.substring(1));
    const ref = query.ref || null;
    const signUpCode = query.code || null;
    const company = query.company ? decodeURI(query.company) : null;
    if (ref) {
      fetchRef(ref);
    }
    if (signUpCode) {
      fetchPromo(signUpCode);
    }
    this.state = {
      name: null,
      email: query.email ? decodeURI(query.email) : null,
      password: null,
      hideCompany: company || ref,
      company,
      companyUrl: company,
      termsAccepted: false,
      isLoading: null,
      plan: query.plan ? decodeURI(query.plan) : null,
      ref,
      signUpCode,
      menuOpen: false,
      refRun: false,
      benchmarksSignup: query.benchmarks ? decodeURI(query.benchmarks) : null,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { refData } = this.props;
    const { refRun } = this.state;

    // check if ref data received
    // If so then replace what is in company
    if (refData && !refRun && refData.company) {
      // this should only be reached once in the entire lifecycle of the component
      // because we only call fetchRef once when component constructed
      this.setState({
        company: refData.company,
        companyUrl: refData.company,
        hideCompany: true,
        refRun: true,
      });
    }
  }

  signUpLoading = (type) => {
    const { redirectToProvider } = this.props;
    const { signUpCode } = this.state;
    return () => {
      this.setState({ isLoading: type });
      redirectToProvider({ provider: type, signUpCode });
    };
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const { developerSignUp } = this.props;
    const {
      name,
      email,
      password,
      company,
      companyUrl,
      termsAccepted,
      plan,
      signUpCode,
      ref,
      benchmarksSignup,
    } = this.state;
    developerSignUp(
      name,
      email,
      password,
      company,
      companyUrl,
      termsAccepted,
      plan,
      signUpCode,
      ref,
      benchmarksSignup
    );
  };

  handleChange = (name) => (event) => {
    this.setState({ [name]: event.target.value });
  };

  handleToggleCheckbox = () => {
    const { termsAccepted } = this.state;
    this.setState({ termsAccepted: !termsAccepted });
  };

  getSignInLink = () => {
    let uri = '/login';
    const { ref } = this.state;
    if (ref) {
      uri += `?ref=${ref}`;
    }
    return uri;
  };

  // Company select input functions
  handleCompanyChange = (inputValue, { action }) => {
    if (action === 'input-change') {
      this.setState({ company: inputValue, menuOpen: true });
    } else {
      this.setState({ menuOpen: false });
    }
  };

  getCompanies = (data, input) => {
    if (!data) {
      return;
    } else if (data.length === 0) {
      return [
        {
          label: input,
          logo: null,
          value: input,
          domain: null,
        },
      ];
    }
    return data.map((company) => {
      return {
        label: company.name,
        logo: company.logo,
        value: company.name,
        domain: company.domain,
      };
    });
  };

  loadCompanies = debounce(async (inputValue, callback) => {
    if (!inputValue) {
      return;
    }

    const response = await Api.get(
      `/api/v1/company-search/companies?filter=${inputValue}`
    );
    if (response.status === 200) {
      return callback(this.getCompanies(JSON.parse(response.data), inputValue));
    }
  }, DEBOUNCE_INTERVAL);

  selectCompany = (option) => {
    if (!option) {
      this.setState({ company: '', companyUrl: '' });
      return;
    }
    this.setState({ company: option.value, companyUrl: option.domain });
  };

  render() {
    const {
      isLoggedIn,
      isLoadingAccount,
      error,
      isLoggingIn,
      promo,
      theme,
      classes,
    } = this.props;
    const {
      name,
      email,
      password,
      company,
      companyUrl,
      termsAccepted,
      isLoading,
      menuOpen,
      hideCompany,
    } = this.state;

    if (isLoggedIn) {
      return <Redirect to="/dash" />;
    } else if (isLoadingAccount) {
      return null;
    }

    let errorMsg = error;
    if (error === 'USER_ALREADY_EXISTS') {
      errorMsg =
        'A user with that email already exists.\nPlease log in to continue.';
    }
    const hasPromo = promo && (promo.photo_url || promo.message);
    return (
      <Div height="100vh" width="100vw" style={{ overflowY: 'auto' }}>
        <UnauthedHeader />
        <Text variant="h1" textAlign="center" mt={10} mb={4}>
          Sign Up
        </Text>
        <Card display="block" width={400} m="auto" py={4} px={5} mb={6}>
          {hasPromo && (
            <Flex
              id="promoContainer"
              mb={2}
              pb={2}
              borderBottom={`2px solid ${theme.palette.grey[800]}`}
            >
              {promo.photo_url && (
                <Col p={0} mr={4} className={classes.promoPhoto}>
                  <img src={promo.photo_url} alt="" />
                </Col>
              )}
              <Text variant="body1">
                <small>{promo.message}</small>
              </Text>
            </Flex>
          )}
          {/*GitHub Button*/}
          <OAuthButton
            provider={'github'}
            onClick={this.signUpLoading('github')}
            isLoading={isLoading}
          />
          {/*GitLab Button*/}
          <OAuthButton
            provider={'gitlab'}
            onClick={this.signUpLoading('gitlab')}
            isLoading={isLoading}
          />
          {/*Bitbucket Button*/}
          <OAuthButton
            provider={'bitbucket'}
            onClick={this.signUpLoading('bitbucket')}
            isLoading={isLoading}
          />
          {/*Azure DevOps Button*/}
          <OAuthButton
            provider={'azuredevops'}
            onClick={this.signUpLoading('azuredevops')}
            isLoading={isLoading}
          />
          {/*HackerOne Button*/}
          {isOn(
            GlobalFeatureFlag.CLIENT_DISABLE_HACKERONE_OAUTH_LOGIN
          ) ? undefined : (
            <OAuthButton
              provider={'hackerone'}
              onClick={this.signUpLoading('hackerone')}
              isLoading={isLoading}
            />
          )}
          <Flex width="100%" alignItems="center" justifyContent="center">
            <div className={classes.line} />
            <Text
              variant="h3"
              fontWeight={theme.typography.fontWeightRegular}
              mb={2}
              color="textSecondary"
              mx={2}
            >
              or
            </Text>
            <div className={classes.line} />
          </Flex>
          {error && (
            <Text
              variant="body1"
              id="signUpAlert"
              color="error"
              py={1}
              align="center"
            >
              {errorMsg}
            </Text>
          )}
          <form onSubmit={this.handleSubmit}>
            <InputField
              id="name"
              fullWidth
              mb={2}
              placeholder="Name"
              required={true}
              type="text"
              value={name || ''}
              onChange={this.handleChange('name')}
            />
            <InputField
              id="email"
              fullWidth
              mb={2}
              placeholder="Work Email"
              required={true}
              type="email"
              value={email || ''}
              onChange={this.handleChange('email')}
            />
            <Div mb={password ? 1 : 2}>
              <InputField
                id="password"
                fullWidth
                placeholder="Password"
                required={true}
                type="password"
                minLength="8"
                value={password || ''}
                onChange={this.handleChange('password')}
              />
              <Suspense
                fallback={
                  password ? (
                    <Div height={26}>
                      <ContentLoader height={'2px'} width={'388px'} />
                    </Div>
                  ) : null
                }
              >
                {password && (
                  <PasswordStrengthBar password={password} minLength={8} />
                )}
              </Suspense>
            </Div>
            {!hideCompany && (
              <React.Fragment>
                <CompanyFilter
                  inputValue={company || ''}
                  onInputChange={this.handleCompanyChange}
                  menuIsOpen={menuOpen}
                  onBlur={() => this.setState({ menuOpen: false })}
                  onChange={this.selectCompany}
                  loadOptions={this.loadCompanies}
                />
                <InputField
                  id="companyUrl"
                  fullWidth
                  mb={2}
                  placeholder="Company URL"
                  required={true}
                  type="text"
                  value={companyUrl || ''}
                  onChange={this.handleChange('companyUrl')}
                />
              </React.Fragment>
            )}
            <Flex alignItems="center" mb={4}>
              <Input
                id="termsAccepted"
                height="15px"
                width="15px"
                mr={2}
                required={true}
                type="checkbox"
                checked={termsAccepted}
                onChange={this.handleToggleCheckbox}
              />
              <label htmlFor="termsAccepted">
                <Text variant="body1" color="textSecondary">
                  {`I agree to the `}
                  <Link
                    href="https://www.pullrequest.com/terms"
                    target="_blank"
                  >
                    Terms of Service
                  </Link>
                  {` and `}
                  <Link
                    href="https://www.pullrequest.com/privacy"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </Text>
              </label>
            </Flex>
            {/*TODO Add disabled logic*/}
            <Button
              width="100%"
              type="submit"
              color="primary"
              variant="contained"
            >
              {isLoggingIn ? (
                <Flex center justifyContent="center">
                  <Loading size={24} customcolor={theme.palette.common.white} />
                </Flex>
              ) : (
                'Sign Up'
              )}
            </Button>
          </form>
          <Flex
            justifyContent="center"
            borderTop={`solid 1px ${theme.palette.divider}`}
            mt={3}
            pt={3}
          >
            <Text
              variant="body1"
              color="textSecondary"
              fontWeight={theme.typography.fontWeightLight}
            >
              Already have an account?{' '}
              <Link href={this.getSignInLink()}>Sign In</Link>
            </Text>
          </Flex>
        </Card>
      </Div>
    );
  }
}

DeveloperSignUp.propTypes = {
  isLoadingAccount: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  isLoggingIn: PropTypes.bool.isRequired,
  error: PropTypes.string,
  location: PropTypes.object.isRequired,
  signup: PropTypes.func.isRequired,
  fetchRef: PropTypes.func.isRequired,
  fetchPromo: PropTypes.func.isRequired,
  refData: PropTypes.object,
  promo: PropTypes.object,
  redirectToProvider: PropTypes.func,
};

function mapStateToProps(state) {
  const { signups } = state;
  const account = accountSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  return {
    isLoadingAccount,
    isLoggedIn: !!account.user,
    isLoggingIn: signups.isLoading,
    error: signups.error,
    refData: signups.ref,
    promo: signups.promo,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...signupsActions,
      redirectToProvider: generators.oauth.handleOauthRedirect.request,
    },
    dispatch
  );
}

export default withTheme(
  withStyles(styles)(
    connect(mapStateToProps, mapDispatchToProps)(DeveloperSignUp)
  )
);
